<template>
  <b-form novalidate>
    <form-element :label="$t('cmrad.users_uppercase')" :validation="$v.recipients">
      <form-tag-input
        v-model="tags"
        :placeholder="$t('cmrad.add_username_or_email_try_@')"
        :rules="rules"
        class="my-2"
        variant="mention"
        :validation="$v.recipients"
        :is-filterable="false"
      />
      <template slot="required">Users required</template>
      <template slot="invalid">Invalid username found</template>
    </form-element>

    <form-element :label="$t('cmrad.comment')" :validation="$v.comment">
      <template slot="label">
        <character-count :value="comment" :max-length="$v.comment.$params.maxLength.max" />
      </template>
      <rich-editor
        ref="commentEditor"
        v-model="commentHtml"
        output="text"
        :lines="4"
        :max-lines="10"
        :validation="$v.comment"
        :related-case="sharedCase"
        :images="sharedCase.keyImages"
      />
    </form-element>
  </b-form>
</template>

<script>
  import { validationMixin } from 'vuelidate';
  import { formValidationMixin } from '../validators';
  import { CaseResource } from '@/api-resources';
  import { normalizeString, htmlToText } from '../filters';
  import { maxLength, required, requiredIf, email } from 'vuelidate/lib/validators';
  import CharacterCount from '@/Atoms/CharacterCount';
  import FormElement from '@/Molecules/FormElement';
  import toastr from 'toastr';
  import FormTagInput from '@/Atoms/FormTagInput';

  const MESSAGE_MAX_LENGTH = 500;
  const USERNAME_REGEX = /^@[0-9a-z_-]+$/i; // TODO: Extract this to a module and make it global
  const EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  export default {
    name: 'ShareCaseForm',
    mixins: [validationMixin, formValidationMixin],

    components: {
      CharacterCount,
      FormElement,
      FormTagInput,
    },

    props: {
      sharedCase: CaseResource,
    },

    data() {
      return {
        commentHtml: null,
        tags: [],
      };
    },

    computed: {
      rules() {
        return [USERNAME_REGEX, EMAIL_REGEX];
      },

      comment() {
        return htmlToText(normalizeString(this.commentHtml));
      },

      message() {
        return {
          recipients: this.recipients,
          comment: this.commentHtml,
        };
      },

      recipients() {
        return this.tags.map((item) => {
          if (USERNAME_REGEX.test(item)) {
            return { username: item.slice(1) };
          } else if (EMAIL_REGEX.test(item)) {
            return { email: item };
          }

          return { id: null };
        });
      },
    },

    validations: {
      recipients: {
        required,
        $each: {
          id: {
            required: requiredIf(function (user) {
              return !user.username && !user.email;
            }),
          },
          username: {
            required: requiredIf(function (user) {
              return !user.id && !user.email;
            }),
          },
          email: {
            required: requiredIf(function (user) {
              return !user.id && !user.username;
            }),
            email,
          },
        },
      },

      comment: {
        maxLength: maxLength(MESSAGE_MAX_LENGTH),
      },
    },

    methods: {
      reset() {
        this.tags = [];
        this.$refs.commentEditor.clearContent();
        this.$v.$reset();
      },

      submit() {
        this.$v.$touch();

        if (this.$v.$invalid) {
          toastr.error('Please correct invalid fields');
          return Promise.reject('Invalid form');
        }

        return this.$api('cases.share', ['1.0', this.sharedCase], this.message)
          .then(() => {
            toastr.success('Case has been shared');
            this.reset();
          })
          .catch(() => {
            toastr.error('Case sharing failed');
          });
      },
    },
  };
</script>

<style scoped></style>
